import React, { useEffect, useRef } from 'react'
import { EditorContent, useEditor, type Editor } from '@tiptap/react'
import { Placeholder, UndoRedo } from '@tiptap/extensions'
import { canvasTextExtensions } from '../ProjectCanvas/utils/canvasTextSchema'
import { PROJECT_TITLE_PALETTE } from './titleTextPalette'
import { TITLE_PREVIEW_META } from './titleFormat'
// Die Marker-Formen und Auszeichnungen teilt der Titel mit dem Text der Pinnwand.
import '../ProjectCanvas/nodes/TextNode.scss'

export interface ProjectTitleEditorProps {
  /** Der Titel als HTML (ADR 0030). */
  value: string
  placeholder: string
  label: string
  /** Nach jeder Eingabe: das HTML und sein Klartext. */
  onValueChange: (html: string, plain: string) => void
  /** Enter — der Titel ist fertig. */
  onSubmit: () => void
  /** Escape — verwerfen. */
  onCancel: () => void
  /** ↓ am Ende des Titels — weiter zu den Notizen bzw. in die Liste. */
  onArrowDownAtEnd: () => void
  onFocus?: () => void
  /** Der Editor steht (bzw. geht, `null`) — das Titelwerkzeug formatiert über ihn. */
  onReady?: (editor: Editor | null) => void
  /** Das Schreibfeld selbst (ProseMirror-Element) — für Fokus, Maße und den Breadcrumb. */
  fieldRef?: React.RefObject<HTMLElement | null>
}

/** ⌘/Strg-Kombinationen, die dem Text gehören: Auszeichnungen, Zwischenablage, Rückgängig, Alles markieren. */
const TEXT_COMMAND_KEYS = new Set(['a', 'b', 'c', 'i', 'u', 'v', 'x', 'y', 'z'])

/** Ein Titel im Klartext wird zu einem Absatz — mit maskierten Zeichen, damit `<` Text bleibt. */
export function titleHtmlFromPlain(plain: string): string {
  const escaped = plain.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  return `<p>${escaped}</p>`
}

/**
 * Das Schreibfeld des **Projekttitels** — TipTap mit dem Schema der Pinnwand
 * (ADR 0029/0030), aber einzeilig, ohne Listen und mit der Titel-Palette.
 *
 * Es sieht aus wie eine Überschrift und ist immer schreibbar, wie das
 * `<textarea>` vorher. ⌘B/⌘I/⌘U/⇧⌘S wirken auch ohne offenes Titelwerkzeug.
 *
 * @example
 * <ProjectTitleEditor value={html} placeholder="Neues Projekt" label="Projekttitel" … />
 */
export const ProjectTitleEditor: React.FC<ProjectTitleEditorProps> = ({
  value,
  placeholder,
  label,
  onValueChange,
  onSubmit,
  onCancel,
  onArrowDownAtEnd,
  onFocus,
  onReady,
  fieldRef,
}) => {
  const handlers = useRef({ onValueChange, onSubmit, onCancel, onArrowDownAtEnd, onFocus })
  handlers.current = { onValueChange, onSubmit, onCancel, onArrowDownAtEnd, onFocus }

  const editor = useEditor({
    extensions: [
      ...canvasTextExtensions({ palette: PROJECT_TITLE_PALETTE, singleLine: true }),
      UndoRedo,
      Placeholder.configure({ placeholder }),
    ],
    content: value,
    immediatelyRender: true,
    shouldRerenderOnTransaction: false,
    editorProps: {
      attributes: {
        class: 'project-page__title-input project-title__editor',
        'aria-label': label,
        role: 'textbox',
        'aria-multiline': 'false',
      },
      handleDOMEvents: {
        // **Der Fokus im Titel hat Vorrang** (gemeldet 2026-09-25): Die
        // Kürzel der Seite und der Pinnwand horchen am `window` und schweigen
        // nur für `<input>`/`<textarea>` — ein contenteditable kennen sie
        // nicht, „c" öffnete die Pinnwand, „t" griff zum Text-Werkzeug. Wie
        // `CanvasTextEditor` bleibt der Tastendruck deshalb hier; ProseMirror
        // hat ihn da schon verarbeitet. Durch dürfen nur Kombinationen mit
        // ⌘/Strg, die kein Text-Befehl sind (⌘W, ⌘L, ⌘S …).
        keydown: (_view, event) => {
          const mod = event.metaKey || event.ctrlKey
          const textCommand = mod && (TEXT_COMMAND_KEYS.has(event.key.toLowerCase()) || (event.shiftKey && event.key.toLowerCase() === 's'))
          if (!mod || textCommand) event.stopPropagation()
          return false
        },
      },
      handleKeyDown: (view, event) => {
        if (event.key === 'Enter') {
          event.preventDefault()
          handlers.current.onSubmit()
          return true
        }
        if (event.key === 'Escape') {
          event.preventDefault()
          handlers.current.onCancel()
          return true
        }
        if (event.key === 'ArrowDown' && view.state.selection.empty && view.state.selection.to === view.state.doc.content.size - 1) {
          event.preventDefault()
          handlers.current.onArrowDownAtEnd()
          return true
        }
        return false
      },
    },
    onUpdate: ({ editor, transaction }) => {
      if (transaction.getMeta(TITLE_PREVIEW_META)) return
      handlers.current.onValueChange(editor.getHTML(), editor.getText())
    },
    onFocus: () => handlers.current.onFocus?.(),
  })

  // Ein Wert von außen (Laden, anderes Gerät, Verwerfen) — nie mitten im Schreiben.
  useEffect(() => {
    if (!editor || editor.isFocused || editor.getHTML() === value) return
    editor.commands.setContent(value, { emitUpdate: false })
  }, [editor, value])

  useEffect(() => {
    if (!fieldRef) return
    ;(fieldRef as React.MutableRefObject<HTMLElement | null>).current = editor ? (editor.view.dom as HTMLElement) : null
  }, [editor, fieldRef])

  const readyRef = useRef(onReady)
  readyRef.current = onReady
  useEffect(() => {
    readyRef.current?.(editor)
    return () => readyRef.current?.(null)
  }, [editor])

  return <EditorContent editor={editor} className="project-title__editor-host" />
}
